// @vendors
const Immutable = require('immutable');
const { get } = require('lodash');
const {
    CONTRIBUTION_PERIOD_MONTHLY,
    MINIMUM_PERIODIC_CONTRIBUTION,
    MINIMUM_MANAGEMENT_PLAN_INVESTMENT_AMOUNT,
} = require('../raConstants/index');

//@helpers
const PersonalDataHelper = require('utilities/personalDataHelper');

// @constants
const {
    FIRST_STEP,
    SECOND_STEP,
    THIRD_STEP,
    FOURTH_STEP,
    FIFTH_STEP,
    BROKER_TRANSFER_FUNDS_ALL,
} = require('constants/index');
const {
    SIGNATURE_PATTERN_SMS_VALIDATION_FAILURE,
} = require('constants/actionTypes');
const actionTypes = require('../raConstants/actionTypes');
const {
    RA_INVESTMENT_TYPE_MONEY,
    ORIGIN_COUNTRY_GERMANY,
} = require('../raConstants/index');

// utilities
const { getDay, todayDate } = require('utilities/dateHelper');

const initialState = Immutable.fromJS({
    selectedAccountsWithFunds: {},
    fundsTotalAmount: 0,
    steps: [
        {
            investmentAmount: 0,
            investmentType: RA_INVESTMENT_TYPE_MONEY,
            selectedAccount: BROKER_TRANSFER_FUNDS_ALL,
            valid: true,
            errorFlags: false,
        },
        {
            periodicity: CONTRIBUTION_PERIOD_MONTHLY,
            recurringContributionAmount: '',
            contributionDay: getDay(todayDate()),
            errorFlags: false,
            valid: false,
        },
        {
            selectedAccount: '',
            accountBalance: {
                amount: 0,
                currency: 'EUR',
            },
            errorFlags: false,
            valid: false,
        },
        {
            emailConfigured: false,
            email: '',
            emailBlur: '',
            repeatEmail: '',
            repeatEmailBlur: '',
            errorFlags: false,
            valid: false,
            emailDirty: false,
            repeatEmailDirty: false,
        },
        {
            disabledCheck: true,
            disclaimerChecked: false,
            errorFlags: false,
            valid: false,
        },
    ],
    visibleStep: FIRST_STEP,
    submitSuccessful: false,
    isFetching: false,
    isDistributionModalOpen: false,
    pdfLink: null,
    pdfContent: null,
    pdfConcat: null,
    pdfConcatURL: null,
    displaySignaturePattern: false,
    annualCost: '0',
    productCost: 0,
    displayErrorScreen: false,
    generatedPdf: null,
    fetchingPdf: false,
    errorOnPdf: false,
    operationProcessIdFetching: false,
    operationProcessIdError: false,
    operationProcessId: null,
    termsAndConditions: '',
});

const validateSelectedAccount = (accountBalance, state, originCountry) => {
    let valid =
        accountBalance.get('amount') >=
        state.getIn(['steps', FIRST_STEP, 'investmentAmount']);

    if (originCountry === ORIGIN_COUNTRY_GERMANY && __CONTACT_CENTER__) {
        valid = accountBalance.get('amount') >= 0;
    }
    return valid;
};

function validateEmailForm(state) {
    // we check that the user entered data in both inputs
    return (
        state.getIn(['steps', FOURTH_STEP, 'emailDirty']) &&
        state.getIn(['steps', FOURTH_STEP, 'repeatEmailDirty']) &&
        // we check that it has the right format
        PersonalDataHelper.isValidEmail(
            state.getIn(['steps', FOURTH_STEP, 'email']),
        ) &&
        // we check that new email and repeated new email are the same
        state.getIn(['steps', FOURTH_STEP, 'email']) ===
            state.getIn(['steps', FOURTH_STEP, 'repeatEmail'])
    );
}

function validateUIStep(state, step) {
    switch (step) {
        case FIRST_STEP:
        case SECOND_STEP:
        case THIRD_STEP:
        case FOURTH_STEP:
            return state.setIn(['steps', step, 'valid'], true);
        case FIFTH_STEP: {
            if (__CONTACT_CENTER__) {
                return state.setIn(['steps', step, 'valid'], true);
            }

            const disclaimerChecked = state.getIn([
                'steps',
                step,
                'disclaimerChecked',
            ]);
            return state.setIn(['steps', step, 'valid'], disclaimerChecked);
        }
        default:
            return state;
    }
}

const RAHireManagerPlan = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_CHANGE_TRANSFER_FUNDS_ACCOUNT:
            return state.mergeIn(['steps', FIRST_STEP], {
                selectedAccount: action.payload,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_CHANGE_INVESTMENT_TYPE:
            const investmentAmount = state.getIn([
                'steps',
                FIRST_STEP,
                'investmentAmount',
            ]);
            const fundsTotalAmount = state.get('fundsTotalAmount');
            let newState = state;
            if (action.payload === RA_INVESTMENT_TYPE_MONEY) {
                newState = state
                    .set('selectedAccountsWithFunds', Immutable.Map())
                    .set('fundsTotalAmount', 0)
                    .setIn(['steps', THIRD_STEP, 'selectedAccount'], '');
            }

            return newState.mergeIn(['steps', FIRST_STEP], {
                investmentType: action.payload,
                valid:
                    action.payload === RA_INVESTMENT_TYPE_MONEY
                        ? investmentAmount >=
                          MINIMUM_MANAGEMENT_PLAN_INVESTMENT_AMOUNT
                        : fundsTotalAmount >=
                          MINIMUM_MANAGEMENT_PLAN_INVESTMENT_AMOUNT,
            });

        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_CONFIGURE_EMAIL: {
            const valid = validateEmailForm(state);
            const userEmailInfo = action.payload;
            return state.mergeIn(['steps', FOURTH_STEP], {
                ...userEmailInfo,
                valid,
            });
        }
        case actionTypes.RA_HIRE_MANAGEMENT_EXPLICIT_IMPLICIT_COST_RETRIEVED:
            const { data } = action.payload;
            return state.merge({
                annualCost: get(data, 'explicitCosts'),
                annualCostExclVat: get(data, 'explicitCostsExclVat'),
                annualCostInclVat: get(data, 'explicitCostsInclVat'),
                productCost: get(data, 'implicitCosts'),
            });
        case actionTypes.RA_HIRE_MANAGEMENT_EXPLICIT_IMPLICIT_COST_ERROR:
            return state.merge({
                isFetching: false,
                error: action.payload.error,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_INVESTMENT_AMOUNT_CHANGED:
            const errorFlags =
                action.payload.value === undefined
                    ? true
                    : action.payload.value <
                      MINIMUM_MANAGEMENT_PLAN_INVESTMENT_AMOUNT
                    ? true
                    : false;
            return state.mergeIn(['steps', FIRST_STEP], {
                investmentAmount: action.payload.value,
                errorFlags: errorFlags,
                valid: !errorFlags,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SELECTED_ACCOUNT_CHANGED:
            const valid =
                state.getIn(['steps', FIRST_STEP, 'investmentType']) !==
                    RA_INVESTMENT_TYPE_MONEY ||
                validateSelectedAccount(
                    action.payload.accountBalance,
                    state,
                    action.payload.originCountry,
                );
            return state.mergeIn(['steps', THIRD_STEP], {
                accountBalance: action.payload.accountBalance,
                selectedAccount: action.payload.accountId,
                valid,
                errorFlags: !valid,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_CREATE_ACCOUNT_ERROR:
            return state.merge({
                isFetching: false,
                error: action.payload.error,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_CREATE_ACCOUNT_REQUEST:
            return state.merge({
                isFetching: true,
            });
        case SIGNATURE_PATTERN_SMS_VALIDATION_FAILURE:
            return state.merge({
                isFetching: false,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_RESET:
            return initialState;
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_RESET_STEP:
            return state.setIn(['steps', action.payload.step, 'valid'], false);
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_VISIBLE_STEP:
            const { step } = action.payload;
            newState = state;
            const recurringAmount = state.getIn([
                'steps',
                SECOND_STEP,
                'recurringContributionAmount',
            ]);
            if (step === 2) {
                if (recurringAmount === 0 || recurringAmount === '') {
                    newState = newState.mergeIn(['steps', SECOND_STEP], {
                        valid: false,
                    });
                }
                const selectedAccount = state.getIn([
                    'steps',
                    THIRD_STEP,
                    'selectedAccount',
                ]);
                const accountBalance = state.getIn([
                    'steps',
                    THIRD_STEP,
                    'accountBalance',
                ]);
                const validBalance = validateSelectedAccount(
                    accountBalance,
                    state,
                );
                const isHiringWithFunds =
                    state.getIn(['steps', FIRST_STEP, 'investmentType']) !==
                    RA_INVESTMENT_TYPE_MONEY;
                newState = newState.mergeIn(['steps', THIRD_STEP], {
                    valid:
                        isHiringWithFunds ||
                        (selectedAccount !== '' && validBalance),
                    errorFlags:
                        !isHiringWithFunds &&
                        selectedAccount !== '' &&
                        !validBalance,
                });
            } else {
                if (step === FIFTH_STEP) {
                    const disclaimerChecked = state.getIn([
                        'steps',
                        FIFTH_STEP,
                        'disclaimerChecked',
                    ]);
                    const isValid = __CONTACT_CENTER__
                        ? true
                        : disclaimerChecked;
                    newState = newState.mergeIn(['steps', FIFTH_STEP], {
                        valid: isValid,
                    });
                }
            }
            return newState.merge({
                visibleStep: step,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_VALIDATE_UI_STEP:
            return validateUIStep(state, state.get('visibleStep'));
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_FINISH:
            return state.merge({
                submitSuccessful: true,
                isFetching: false,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_UPDATE_SELECTED_ACCOUNTS_WITH_FUNDS:
            return state.set(
                'selectedAccountsWithFunds',
                Immutable.fromJS(action.payload.newSelectedAccountsWithFunds),
            );
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_UPDATE_SELECTED_FUNDS_TOTAL:
            const { newAmount } = action.payload;
            return state
                .merge({
                    fundsTotalAmount: newAmount,
                })
                .mergeIn(['steps', FIRST_STEP], {
                    valid: newAmount >= 500,
                });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_ENABLE_DISCLAIMER_CHECK:
            return state.mergeIn(['steps', FIFTH_STEP], {
                disabledCheck: false,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_TOGGLE_DISCLAIMER:
            return state
                .updateIn(
                    ['steps', FIFTH_STEP, 'disclaimerChecked'],
                    disclaimerChecked => !disclaimerChecked,
                )
                .merge({
                    displaySignaturePattern: true,
                });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_EMAIL_CONFIG_ERROR:
            return state
                .mergeIn(
                    ['steps', FOURTH_STEP],
                    initialState.getIn(['steps', FOURTH_STEP]),
                )
                .merge({
                    displayErrorScreen: true,
                });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_PDF:
            return state.merge({
                pdfLink: action.payload.pdf,
                pdfContent: action.payload.pdfContent,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_CONCAT_PDF:
            return state.merge({
                pdfConcat: action.payload.pdfConcat,
                pdfConcatURL: action.payload.pdfConcatURL,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_RECURRING_CONTRIBUTION_AMOUNT:
            const errorFlag =
                action.payload.amount !== ''
                    ? action.payload.amount < MINIMUM_PERIODIC_CONTRIBUTION
                        ? true
                        : false
                    : false;
            const validFlag =
                state.get('visibleStep') === FIRST_STEP ? false : !errorFlag;
            return state.mergeIn(['steps', SECOND_STEP], {
                recurringContributionAmount: action.payload.amount,
                errorFlags: errorFlag,
                valid: validFlag,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_PERIODICITY:
            return state.mergeIn(['steps', SECOND_STEP], {
                periodicity: action.payload.periodicity,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SET_CONTRIBUTIONDAY:
            return state.mergeIn(['steps', SECOND_STEP], {
                contributionDay: action.payload.contributionDay,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_CONFIRM_GET_PDF_FETCHING:
            return state.merge({
                fetchingPdf: true,
                errorOnPdf: false,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_CONFIRM_GET_PDF_SUCCESS:
            return state.merge({
                generatedPdf: action.payload.generatedPdf,
                fetchingPdf: false,
                errorOnPdf: false,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_CONFIRM_GET_PDF_FAILURE:
            return state.merge({
                generatedPdf: null,
                fetchingPdf: false,
                errorOnPdf: true,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_DMS_INIT_FETCHING:
            return state.merge({
                operationProcessIdFetching: true,
                operationProcessIdError: false,
                operationProcessId: null,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_DMS_INIT_SUCCESS:
            return state.merge({
                operationProcessIdFetching: false,
                operationProcessIdError: false,
                operationProcessId: action.payload.operationProcessId,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_DMS_INIT_ERROR:
            return state.merge({
                operationProcessIdFetching: false,
                operationProcessIdError: true,
                operationProcessId: null,
            });
        case actionTypes.RA_HIRE_MANAGEMENT_PLAN_SAVE_TERMS_AND_CONDITIONS:
            return state.merge({
                termsAndConditions: action.payload,
            });
        default:
            return state;
    }
};

module.exports = RAHireManagerPlan;
